document.documentElement.classList.remove('no-js');

document.addEventListener('DOMContentLoaded', () => {
  const menuButton = document.querySelector('[data-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  if (menuButton && mobileMenu) {
    const closeMobileMenu = () => {
      menuButton.setAttribute('aria-expanded', 'false');
      menuButton.setAttribute('aria-label', 'Open menu');
      mobileMenu.hidden = true;
    };
    menuButton.addEventListener('click', () => {
      const open = menuButton.getAttribute('aria-expanded') === 'true';
      menuButton.setAttribute('aria-expanded', String(!open));
      menuButton.setAttribute('aria-label', open ? 'Open menu' : 'Close menu');
      mobileMenu.hidden = open;
      if (!open) mobileMenu.querySelector('a, summary')?.focus();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || mobileMenu.hidden) return;
      closeMobileMenu();
      menuButton.focus();
    });
  }

  document.querySelectorAll('[data-hero-carousel]').forEach((carousel) => {
    const slides = Array.from(carousel.querySelectorAll('[data-hero-slide]'));
    const dots = Array.from(carousel.querySelectorAll('[data-hero-dot]'));
    const previous = carousel.querySelector('[data-hero-previous]');
    const next = carousel.querySelector('[data-hero-next]');
    const pause = carousel.querySelector('[data-hero-pause]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const interval = Number(carousel.dataset.autoplay) || 7000;
    let current = 0;
    let timer;
    let manuallyPaused = false;

    if (slides.length < 2) return;

    const showSlide = (index, restart = true) => {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-pressed', String(active));
      });
      if (restart) startAutoplay();
    };

    const stopAutoplay = () => window.clearInterval(timer);
    const startAutoplay = () => {
      stopAutoplay();
      if (!reduceMotion && !manuallyPaused) timer = window.setInterval(() => showSlide(current + 1, false), interval);
    };

    previous?.addEventListener('click', () => showSlide(current - 1));
    next?.addEventListener('click', () => showSlide(current + 1));
    dots.forEach((dot) => dot.addEventListener('click', () => showSlide(Number(dot.dataset.heroDot))));
    pause?.addEventListener('click', () => {
      manuallyPaused = !manuallyPaused;
      pause.setAttribute('aria-pressed', String(manuallyPaused));
      pause.setAttribute('aria-label', manuallyPaused ? 'Play homepage banners' : 'Pause homepage banners');
      pause.textContent = manuallyPaused ? 'Play' : 'Pause';
      manuallyPaused ? stopAutoplay() : startAutoplay();
    });
    carousel.addEventListener('mouseenter', stopAutoplay);
    carousel.addEventListener('mouseleave', startAutoplay);
    carousel.addEventListener('focusin', stopAutoplay);
    carousel.addEventListener('focusout', (event) => {
      if (!carousel.contains(event.relatedTarget)) startAutoplay();
    });
    document.addEventListener('visibilitychange', () => document.hidden ? stopAutoplay() : startAutoplay());
    startAutoplay();
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const main = gallery.querySelector('.product-gallery__main img');
    const conditionRibbon = gallery.querySelector('[data-condition-ribbon]');
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    const thumbsTrack = gallery.querySelector('[data-gallery-thumbs-track]');
    const previousThumbs = gallery.querySelector('[data-gallery-previous]');
    const nextThumbs = gallery.querySelector('[data-gallery-next]');
    const zoomButton = gallery.querySelector('[data-gallery-zoom]');
    const mainFrame = gallery.querySelector('.product-gallery__main');
    let panX = 0;
    let panY = 0;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let panStartX = 0;
    let panStartY = 0;
    let isDragging = false;
    let didDrag = false;
    let suppressImageClick = false;

    const zoomScale = () => window.matchMedia('(max-width: 520px)').matches ? 1.25 : 1.4;
    const clampPan = (value, axis) => {
      const scale = zoomScale();
      const size = axis === 'x' ? mainFrame?.clientWidth || 0 : mainFrame?.clientHeight || 0;
      const limit = Math.max(0, (size * (scale - 1)) / 2);
      return Math.max(-limit, Math.min(limit, value));
    };
    const renderPan = () => {
      if (!main || !mainFrame) return;
      const scale = mainFrame.classList.contains('is-zoomed') ? zoomScale() : 1;
      main.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${scale})`;
    };
    const resetPan = () => {
      panX = 0;
      panY = 0;
      renderPan();
    };

    const setZoom = (zoomed) => {
      if (!mainFrame) return;
      mainFrame.classList.toggle('is-zoomed', zoomed);
      if (!zoomed) resetPan();
      else renderPan();
      if (zoomButton) {
        zoomButton.textContent = zoomed ? '−' : '+';
        zoomButton.setAttribute('aria-pressed', String(zoomed));
        zoomButton.setAttribute('aria-label', zoomed ? 'Zoom out of product image' : 'Zoom in on product image');
      }
    };

    const keepThumbVisible = (thumb) => {
      if (!thumbsTrack || !thumb) return;
      const trackRect = thumbsTrack.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      if (thumbRect.left < trackRect.left) {
        thumbsTrack.scrollBy({ left: thumbRect.left - trackRect.left - 8, behavior: 'smooth' });
      } else if (thumbRect.right > trackRect.right) {
        thumbsTrack.scrollBy({ left: thumbRect.right - trackRect.right + 8, behavior: 'smooth' });
      }
    };

    const showImage = (thumb) => {
      if (!main || !thumb) return;
      setZoom(false);
      main.src = thumb.dataset.src;
      main.srcset = '';
      main.alt = thumb.dataset.alt || '';
      thumbs.forEach((item) => item.classList.remove('is-active'));
      thumb.classList.add('is-active');
      keepThumbVisible(thumb);
      const activeIndex = thumbs.indexOf(thumb);
      if (previousThumbs) previousThumbs.hidden = activeIndex <= 0;
      if (nextThumbs) nextThumbs.hidden = activeIndex >= thumbs.length - 1;
      if (conditionRibbon) {
        const isFirstImage = thumbs[0] === thumb;
        conditionRibbon.hidden = !isFirstImage;
        conditionRibbon.setAttribute('aria-hidden', String(!isFirstImage));
      }
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        showImage(thumb);
      });
    });

    previousThumbs?.addEventListener('click', () => {
      const activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      showImage(thumbs[Math.max(0, activeIndex - 1)]);
    });
    nextThumbs?.addEventListener('click', () => {
      const activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      showImage(thumbs[Math.min(thumbs.length - 1, activeIndex + 1)]);
    });
    mainFrame?.addEventListener('pointerdown', (event) => {
      if (!mainFrame.classList.contains('is-zoomed') || event.target.closest('[data-gallery-zoom]')) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      isDragging = true;
      didDrag = false;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      panStartX = panX;
      panStartY = panY;
      mainFrame.classList.add('is-dragging');
      mainFrame.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    });
    mainFrame?.addEventListener('pointermove', (event) => {
      if (!isDragging) return;
      const deltaX = event.clientX - pointerStartX;
      const deltaY = event.clientY - pointerStartY;
      if (Math.abs(deltaX) + Math.abs(deltaY) > 4) didDrag = true;
      panX = clampPan(panStartX + deltaX, 'x');
      panY = clampPan(panStartY + deltaY, 'y');
      renderPan();
      event.preventDefault();
    });
    const finishDrag = (event) => {
      if (!isDragging) return;
      isDragging = false;
      suppressImageClick = didDrag;
      mainFrame.classList.remove('is-dragging');
      mainFrame.releasePointerCapture?.(event.pointerId);
    };
    mainFrame?.addEventListener('pointerup', finishDrag);
    mainFrame?.addEventListener('pointercancel', finishDrag);
    zoomButton?.addEventListener('click', () => setZoom(!mainFrame?.classList.contains('is-zoomed')));
    main?.addEventListener('click', (event) => {
      if (suppressImageClick) {
        suppressImageClick = false;
        event.preventDefault();
        return;
      }
      setZoom(!mainFrame?.classList.contains('is-zoomed'));
    });
  });

  document.querySelectorAll('[data-product-overview]').forEach((overview) => {
    const tabs = Array.from(overview.querySelectorAll('[data-product-tab]'));
    const panels = Array.from(overview.querySelectorAll('[data-product-panel]'));
    const setPanel = (name, updateHash = true) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.productTab === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.productPanel !== name;
      });
      if (updateHash && window.history?.replaceState) {
        const hash = name === 'specifications' ? '#product-specifications' : name === 'warranty' ? '#product-warranty' : '#product-overview';
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`);
      }
    };
    tabs.forEach((tab) => tab.addEventListener('click', () => setPanel(tab.dataset.productTab)));
    tabs.forEach((tab, index) => tab.addEventListener('keydown', (event) => {
      let targetIndex = null;
      if (event.key === 'ArrowRight') targetIndex = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') targetIndex = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = tabs.length - 1;
      if (targetIndex === null) return;
      event.preventDefault();
      tabs[targetIndex].focus();
      setPanel(tabs[targetIndex].dataset.productTab);
    }));
    const initialHash = window.location.hash;
    if (initialHash === '#product-specifications') setPanel('specifications', false);
    if (initialHash === '#product-warranty') setPanel('warranty', false);
  });

  document.querySelectorAll('[data-product-overview-link]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const overview = document.querySelector('[data-product-overview]');
      const overviewTab = overview?.querySelector('[data-product-tab="overview"]');
      if (!overview || !overviewTab) return;
      event.preventDefault();
      overviewTab.click();
      overview.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  document.querySelectorAll('[data-product-variant-cards]').forEach((picker) => {
    const form = picker.parentElement?.querySelector('form[action*="/cart/add"]');
    const input = picker.querySelector('[data-variant-input]');
    const cards = Array.from(picker.querySelectorAll('[data-variant-card]'));
    if (!form || !input || !cards.length) return;
    cards.forEach((card) => card.addEventListener('click', () => {
      if (card.disabled) return;
      input.value = card.dataset.variantId || '';
      cards.forEach((item) => {
        const selected = item === card;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-checked', String(selected));
      });
    }));
  });

  document.querySelectorAll('[data-sort-by]').forEach((select) => {
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.assign(url.toString());
    });
  });

  document.querySelectorAll('[data-filter-toggle]').forEach((button) => {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!open));
      button.textContent = open ? 'Filter products' : 'Close filters';
      panel.classList.toggle('is-open', !open);
      if (!open) panel.querySelector('input, select, button')?.focus();
    });
  });

  document.querySelectorAll('[data-service-selector]').forEach((selector) => {
    const productForm = selector.parentElement?.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;
    const fulfilmentInputs = Array.from(selector.querySelectorAll('input[name="caw-fulfilment"]'));
    const packageInputs = Array.from(selector.querySelectorAll('input[name="caw-service-package"]'));
    const deliveryOptions = selector.querySelector('[data-delivery-options]');
    const fulfilmentProperty = productForm.querySelector('[data-fulfilment-property]');
    const serviceProperty = productForm.querySelector('[data-service-property]');
    const productHandle = selector.dataset.productHandle || '';
    const productTitle = selector.dataset.productTitle || '';

    const syncServiceState = () => {
      const fulfilment = selector.querySelector('input[name="caw-fulfilment"]:checked');
      let selectedPackage = selector.querySelector('input[name="caw-service-package"]:checked');
      const baseInput = selector.querySelector('.service-choice__base-input');
      // Large products always carry the mandatory two-person delivery charge.
      // If an optional service is deselected, return to that base delivery line.
      if (!selectedPackage && baseInput && fulfilment?.dataset.fulfilment !== 'collection') selectedPackage = baseInput;
      const isCollection = fulfilment?.dataset.fulfilment === 'collection';
      if (deliveryOptions) {
        deliveryOptions.hidden = isCollection;
        deliveryOptions.setAttribute('aria-hidden', String(isCollection));
      }
      selector.querySelectorAll('.service-choice').forEach((choice) => {
        const input = choice.querySelector('input');
        choice.classList.toggle('is-selected', input?.checked === true && (!isCollection || input.name === 'caw-fulfilment'));
      });
      if (fulfilmentProperty) fulfilmentProperty.value = isCollection ? 'Collection from Coventry shop' : 'Local delivery within 30 miles';
      if (serviceProperty) serviceProperty.value = isCollection ? 'Collection only' : (selectedPackage?.dataset.serviceLabel || 'Delivery only');
      packageInputs.forEach((input) => { input.dataset.cawSelected = String(input.checked); });
    };

    fulfilmentInputs.forEach((input) => input.addEventListener('change', syncServiceState));
    // Capture clicks on the whole option row so clicking the blue tick, icon,
    // or label text can also turn an already-selected service off.
    selector.addEventListener('click', (event) => {
      const choice = event.target.closest?.('.service-choice--package');
      const input = choice?.querySelector('input[name="caw-service-package"]');
      if (!input || input.dataset.cawSelected !== 'true') return;
      event.preventDefault();
      event.stopPropagation();
      input.checked = false;
      syncServiceState();
      window.setTimeout(() => {
        input.checked = false;
        syncServiceState();
      }, 0);
    }, true);
    packageInputs.forEach((input) => {
      input.addEventListener('click', (event) => {
        if (input.dataset.cawSelected === 'true') {
          event.preventDefault();
          input.checked = false;
        }
        // Run after the browser has applied the native radio toggle so the
        // visual state and the linked form property stay in sync.
        window.setTimeout(syncServiceState, 0);
      });
      input.addEventListener('change', syncServiceState);
    });
    syncServiceState();

    productForm.addEventListener('submit', async (event) => {
      const fulfilment = selector.querySelector('input[name="caw-fulfilment"]:checked');
      if (fulfilment?.dataset.fulfilment === 'collection') return;
      const selected = selector.querySelector('input[name="caw-service-package"]:checked');
      const baseInput = selector.querySelector('.service-choice__base-input');
      const ids = Array.from(new Set([baseInput?.value || '', selected?.value || '']
        .flatMap((value) => value.split(','))
        .map((id) => id.trim())
        .filter(Boolean)));
      if (!ids.length) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      try {
        const parentToken = `caw-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        const variantId = String(productForm.querySelector('[name="id"]')?.value || '');
        const quantity = Math.max(1, Number(productForm.querySelector('[name="quantity"]')?.value || 1));
        if (!variantId) throw new Error('Product variant is missing');
        const parentProperties = {};
        productForm.querySelectorAll('input[name^="properties["]').forEach((input) => {
          const match = input.name.match(/^properties\[([^\]]+)\]$/);
          if (match) parentProperties[match[1]] = String(input.value);
        });
        parentProperties._caw_parent_token = parentToken;
        if (baseInput) parentProperties._caw_large_delivery_required = 'true';
        const serviceProperties = {
          _caw_service: 'true',
          _caw_parent_token: parentToken,
          _caw_parent_title: productTitle,
          _caw_parent_handle: productHandle,
          _caw_service_label: selected?.dataset.serviceLabel || baseInput?.dataset.serviceLabel || 'Product service'
        };
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('Basket could not be read');
        const currentCart = await cartResponse.json();
        // Always add a fresh parent line with its own token. This preserves separate
        // colour/variant selections instead of treating them as an update to an
        // existing basket line.
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: ids.map((id) => ({
            id,
            quantity,
            properties: String(id) === largeDeliveryVariantId
              ? { ...serviceProperties, _caw_required_service: 'true', _caw_service_label: 'Large-item delivery' }
              : serviceProperties
          })) })
        });
        if (!response.ok) throw new Error('Service could not be added');
        const serviceData = await response.json();
        const productResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity, properties: parentProperties }] })
        });
        if (!productResponse.ok) {
          await Promise.all((serviceData.items || []).map((item) => fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          })));
          throw new Error('Product could not be added');
        }
        window.location.assign('/cart');
      } catch (error) {
        window.alert('We could not add the selected service. Please try again or contact our team.');
      }
    }, true);
  });

  const cleanOrphanedServices = async () => {
    if (!document.querySelector('.cart-table')) return false;
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return false;
      const cart = await response.json();
      const parentTokens = new Set();
      const parentKeys = new Set();
      const serviceLines = [];
      let lastParentKey = '';
      cart.items.forEach((item) => {
        const properties = item.properties || {};
        if (properties._caw_service === 'true') {
          serviceLines.push({
            key: item.key,
            token: properties._caw_parent_token || '',
            parentKey: properties._caw_parent_key || '',
            fallbackParentKey: lastParentKey,
            variantId: String(item.variant_id),
            parentTitle: properties._caw_parent_title || ''
          });
        } else {
          parentKeys.add(item.key);
          lastParentKey = item.key;
          if (properties._caw_parent_token) parentTokens.add(properties._caw_parent_token);
        }
      });

      // Remove services whose product has gone, and enforce one optional
      // service per product. Mandatory large-item delivery is kept separate.
      const orphaned = serviceLines.filter((item) =>
        (/mattress|bed|furniture/i.test(item.parentTitle) || ['58687107465564', '58687094292828'].includes(item.variantId) || ['Packaging removal', 'Recycling + packaging'].includes(item.parentTitle)) ||
        (!(item.token && parentTokens.has(item.token)) && !(item.parentKey && parentKeys.has(item.parentKey)) && !item.fallbackParentKey)
      );
      const seenOptional = new Set();
      const duplicateServices = [];
      serviceLines.forEach((item) => {
        if (item.variantId === largeDeliveryVariantId) return;
        const parentId = item.token || item.parentKey || item.fallbackParentKey;
        if (!parentId) return;
        if (seenOptional.has(parentId)) duplicateServices.push(item);
        else seenOptional.add(parentId);
      });
      const removals = [...orphaned, ...duplicateServices].filter((item, index, list) =>
        list.findIndex((candidate) => candidate.key === item.key) === index
      );
      if (!removals.length) return false;
      await Promise.all(removals.map((item) => fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 })
      })));
      window.location.reload();
      return true;
    } catch (error) {
      // Cart cleanup is best-effort and should never block checkout.
      return false;
    }
  };

  const largeDeliveryVariantId = '55489128202588';

  const productRequiresLargeDelivery = (item) => {
    const properties = item.properties || {};
    if (properties._caw_large_delivery_required === 'true') return true;
    const text = `${item.title || ''} ${item.product_type || ''} ${item.handle || ''}`.toLowerCase();
    if (/american[-\s]?style|american fridge|side[-\s]?by[-\s]?side/.test(text) && /(fridge|freezer)/.test(text)) return true;
    if (/(tall fridge|tall freezer|tall fridge freezer)/.test(text)) return true;
    if (/(ottoman|gas lift)/.test(text) && /bed/.test(text)) return true;
    if (/mattress/.test(text)) return true;
    if (/(double|king|super king)/.test(text) && /(bed|mattress)/.test(text)) return true;
    if (/chest freezer/.test(text)) {
      const capacity = text.match(/(?:^|\D)(\d{3,4})\s*l(?:itre|iter|\b)/);
      if (capacity && Number(capacity[1]) >= 300) return true;
    }
    return false;
  };

  const productIsCollectionOnly = (item) => {
    const properties = item.properties || {};
    const fulfilment = `${properties._caw_fulfilment || ''} ${properties['Requested fulfilment'] || ''} ${properties['Selected service'] || ''}`.toLowerCase();
    return fulfilment.includes('collection');
  };

  const ensureMandatoryLargeDelivery = async () => {
    if (!document.querySelector('.cart-table')) return false;
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return false;
      const cart = await response.json();
      const products = cart.items.filter((item) => (item.properties || {})._caw_service !== 'true');
      const serviceLines = cart.items.filter((item) => (item.properties || {})._caw_service === 'true');
      let changed = false;

      const requiredParents = products.filter((product) => productRequiresLargeDelivery(product) && !productIsCollectionOnly(product));
      const largeDeliveryLines = serviceLines.filter((item) => String(item.variant_id) === largeDeliveryVariantId);
      const serviceMatchesProduct = (service, product) => {
        const serviceProperties = service.properties || {};
        const productProperties = product.properties || {};
        return (serviceProperties._caw_parent_key && serviceProperties._caw_parent_key === product.key) ||
          (serviceProperties._caw_parent_token && productProperties._caw_parent_token && serviceProperties._caw_parent_token === productProperties._caw_parent_token);
      };
      const addLargeDelivery = async (product) => {
        const properties = product.properties || {};
        const parentToken = properties._caw_parent_token || `caw-large-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: largeDeliveryVariantId,
              quantity: 1,
              properties: {
                _caw_service: 'true',
                _caw_required_service: 'true',
                _caw_parent_key: product.key,
                _caw_parent_token: parentToken,
                _caw_parent_title: product.title || '',
                _caw_parent_handle: product.handle || '',
                _caw_service_label: 'Large-item delivery'
              }
            }]
          })
        });
        return addResponse.ok;
      };
      if (requiredParents.length) {
        const canonical = largeDeliveryLines.find((service) => requiredParents.some((product) => serviceMatchesProduct(service, product)));
        if (!canonical) {
          await Promise.all(largeDeliveryLines.map((item) => fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          })));
          if (await addLargeDelivery(requiredParents[0])) changed = true;
        } else {
          const extras = largeDeliveryLines.filter((item) => item !== canonical);
          if (extras.length) {
            await Promise.all(extras.map((item) => fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: item.key, quantity: 0 })
            })));
            changed = true;
          }
        }
      } else if (largeDeliveryLines.length) {
        await Promise.all(largeDeliveryLines.map((item) => fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        })));
        changed = true;
      }
      if (changed) window.location.reload();
      return changed;
    } catch (error) {
      return false;
    }
  };

  document.querySelectorAll('[data-empty-cart]').forEach((button) => {
    button.addEventListener('click', async () => {
      if (!window.confirm('Empty your basket?')) return;
      button.disabled = true;
      try {
        const response = await fetch('/cart/clear.js', {
          method: 'POST',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Cart could not be cleared');
        window.location.assign('/cart');
      } catch (error) {
        button.disabled = false;
        window.alert('We could not empty your basket. Please try again.');
      }
    });
  });

  const groupCartRows = () => {
    const table = document.querySelector('.cart-table');
    const tbody = table?.querySelector('tbody');
    if (!tbody) return;
    const rows = Array.from(tbody.children).filter((row) => row.tagName === 'TR');
    const originalLineOrder = rows.filter((row) => row.dataset.cartLineKey);
    const products = rows.filter((row) => row.hasAttribute('data-cart-product-row'));
    const services = rows.filter((row) => row.classList.contains('cart-service-row'));
    const addons = rows.filter((row) => row.hasAttribute('data-cart-addon-row'));
    const usedServices = new Set();
    const usedAddons = new Set();
    products.forEach((product) => {
      tbody.appendChild(product);
      const key = product.dataset.cartLineKey || '';
      const token = product.dataset.cartProductToken || '';
      const title = product.dataset.cartProductTitle || '';
      services.filter((service) => {
        const parentKey = service.dataset.cartServiceParentKey || '';
        const parentToken = service.dataset.cartServiceParentToken || '';
        const parentTitle = service.dataset.cartServiceParentTitle || '';
        return !usedServices.has(service) && ((key && parentKey === key) || (token && parentToken === token) || (!key && !token && title && parentTitle === title));
      }).forEach((service) => { tbody.appendChild(service); usedServices.add(service); });
      const addon = addons.find((row) => !usedAddons.has(row) && row.dataset.parentKey === key);
      if (addon) { tbody.appendChild(addon); usedAddons.add(addon); }
    });
    services.filter((service) => !usedServices.has(service)).forEach((service) => tbody.appendChild(service));
    addons.filter((addon) => !usedAddons.has(addon)).forEach((addon) => tbody.appendChild(addon));

    const form = table.closest('form');
    form?.addEventListener('submit', (event) => {
      if (event.submitter?.name !== 'update') return;
      form.querySelectorAll('input[name="updates[]"]').forEach((input) => input.removeAttribute('name'));
      form.querySelectorAll('input.caw-cart-update-value').forEach((input) => input.remove());
      originalLineOrder.forEach((row) => {
        const quantity = row.querySelector('.quantity-input');
        if (!quantity) return;
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'updates[]';
        hidden.value = quantity.value;
        hidden.className = 'caw-cart-update-value';
        form.appendChild(hidden);
      });
    }, { once: true });
  };

  const refreshCartAddonState = async () => {
    const rows = Array.from(document.querySelectorAll('[data-cart-addon-row]'));
    if (!rows.length) return;
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const serviceLines = cart.items.filter((item) => (item.properties || {})._caw_service === 'true');
      rows.forEach((row) => {
        const parentKey = row.dataset.parentKey || '';
        const parentToken = row.dataset.parentToken || '';
        const parentTitle = row.dataset.parentTitle || '';
        const linked = serviceLines.filter((item) => {
          const properties = item.properties || {};
          return (parentToken && properties._caw_parent_token === parentToken) ||
            (parentKey && properties._caw_parent_key === parentKey) ||
            (!parentToken && !parentKey && parentTitle && properties._caw_parent_title === parentTitle);
        });
        const optionalLinked = linked.filter((item) => String(item.variant_id) !== largeDeliveryVariantId);
        row.querySelectorAll('[data-cart-addon]').forEach((button) => {
          const ids = (button.dataset.serviceIds || '').split(',').map((id) => id.trim()).filter(Boolean);
          const matchId = button.dataset.serviceMatchId || ids[0] || '';
          button.hidden = optionalLinked.some((item) => String(item.variant_id) === matchId || ids.includes(String(item.variant_id)));
        });
        const visible = Array.from(row.querySelectorAll('[data-cart-addon]')).some((button) => !button.hidden);
        const empty = row.querySelector('[data-cart-addon-empty]');
        if (empty) {
          empty.hidden = visible;
          if (!visible && optionalLinked.length) empty.textContent = 'A service is already selected for this product.';
        }
        const reset = row.querySelector('[data-cart-addon-reset]');
        if (reset) reset.hidden = visible || optionalLinked.length < 1;
      });
    } catch (error) {
      // Add-on availability is best-effort and should not block basket use.
    }
  };

  document.querySelectorAll('[data-cart-addon]').forEach((button) => {
    if ((button.dataset.serviceIds || '').split(',').some((id) => ['58687094292828','58687107465564'].includes(id.trim()))) { button.remove(); return; }
    button.addEventListener('click', async () => {
      const row = button.closest('[data-cart-addon-row]');
      if (!row) return;
      const ids = (button.dataset.serviceIds || '').split(',').map((id) => id.trim()).filter(Boolean);
      if (!ids.length) return;
      button.disabled = true;
      try {
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('Basket could not be read');
        const cart = await cartResponse.json();
        const parentToken = row.dataset.parentToken || `caw-cart-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        const quantity = Math.max(1, Number(row.dataset.parentQuantity || 1));
        const parentIndex = cart.items.findIndex((item) => item.key === row.dataset.parentKey);
        const linked = cart.items.filter((item, index) => {
          const properties = item.properties || {};
          const directMatch = properties._caw_service === 'true' && ((row.dataset.parentToken && properties._caw_parent_token === row.dataset.parentToken) || (row.dataset.parentKey && properties._caw_parent_key === row.dataset.parentKey) || (!row.dataset.parentToken && !row.dataset.parentKey && row.dataset.parentTitle && properties._caw_parent_title === row.dataset.parentTitle));
          // Also catch legacy service lines that sit directly beneath this
          // product but lack reliable linking properties.
          const sequentialMatch = properties._caw_service === 'true' && parentIndex >= 0 && index > parentIndex &&
            !cart.items.slice(parentIndex + 1, index).some((candidate) => (candidate.properties || {})._caw_service !== 'true');
          return directMatch || sequentialMatch;
        });
        const optionalLinked = linked.filter((item) => String(item.variant_id) !== largeDeliveryVariantId);
        await Promise.all(optionalLinked.map((item) => fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        })));
        const properties = {
          _caw_service: 'true',
          _caw_parent_key: row.dataset.parentKey || '',
          _caw_parent_token: parentToken,
          _caw_parent_title: row.dataset.parentTitle || '',
          _caw_parent_handle: row.dataset.parentHandle || '',
          _caw_service_label: button.dataset.serviceLabel || 'Product service'
        };
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: ids.map((id) => ({ id, quantity, properties })) })
        });
        if (!response.ok) throw new Error('Add-on could not be added');
        window.location.reload();
      } catch (error) {
        button.disabled = false;
        window.alert('We could not add that service. Please try again.');
      }
    });
  });

  document.querySelectorAll('[data-cart-addon-reset]').forEach((button) => {
    button.addEventListener('click', async () => {
      const row = button.closest('[data-cart-addon-row]');
      if (!row) return;
      button.disabled = true;
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Basket could not be read');
        const cart = await response.json();
        const linked = cart.items.filter((item) => {
          const properties = item.properties || {};
          return properties._caw_service === 'true' && ((row.dataset.parentToken && properties._caw_parent_token === row.dataset.parentToken) || (row.dataset.parentKey && properties._caw_parent_key === row.dataset.parentKey) || (!row.dataset.parentToken && !row.dataset.parentKey && row.dataset.parentTitle && properties._caw_parent_title === row.dataset.parentTitle));
        });
        const optionalLinked = linked.filter((item) => String(item.variant_id) !== largeDeliveryVariantId);
        await Promise.all(optionalLinked.map((item) => fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: item.key, quantity: 0 }) } )));
        window.location.reload();
      } catch (error) {
        button.disabled = false;
        window.alert('We could not change the selected service. Please try again.');
      }
    });
  });

  groupCartRows();
  const hideSelectedCartServices = () => {
    document.querySelectorAll('[data-cart-addon-row]').forEach((row) => {
      const selectedIds = [];
      let prior = row.previousElementSibling;
      while (prior && !prior.hasAttribute('data-cart-product-row')) {
        if (prior.classList.contains('cart-service-row')) {
          prior.querySelectorAll('a[href*="variant="]').forEach((link) => {
            const match = link.href.match(/[?&]variant=(\d+)/);
            if (match) selectedIds.push(match[1]);
          });
        }
        prior = prior.previousElementSibling;
      }
      row.querySelectorAll('[data-cart-addon]').forEach((button) => {
        const ids = (button.dataset.serviceIds || '').split(',').map((id) => id.trim()).filter(Boolean);
        const matchId = button.dataset.serviceMatchId || ids[0] || '';
        button.hidden = selectedIds.includes(matchId);
      });
    });
  };
  hideSelectedCartServices();
  refreshCartAddonState().then(hideSelectedCartServices);

  cleanOrphanedServices().then((changed) => {
    if (!changed) ensureMandatoryLargeDelivery();
  });
});


// CAW dimension labels
 document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.specifications dd').forEach(function (dd) {
    var text = (dd.textContent || '').trim();
    var match = text.match(/^(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)(\s*cm)?$/i);
    if (!match || dd.dataset.cawDimensions === 'true') return;
    dd.dataset.cawDimensions = 'true';
    var unit = match[4] || ' cm';
    dd.innerHTML = '<strong>H:</strong> ' + match[1] + unit + ' <span aria-hidden="true">×</span> <strong>W:</strong> ' + match[2] + unit + ' <span aria-hidden="true">×</span> <strong>D:</strong> ' + match[3] + unit;
  });
 });


/* Product image lightbox for desktop and mobile. */
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
    var frame = gallery.querySelector('.product-gallery__main');
    var image = frame && frame.querySelector('img');
    var thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    if (!frame || !image || !window.HTMLDialogElement) return;
    var lightbox = document.createElement('dialog');
    lightbox.className = 'product-image-lightbox';
    lightbox.setAttribute('aria-label', 'Product image enlarged');
    lightbox.innerHTML = '<button type="button" class="product-image-lightbox__close" aria-label="Close enlarged image">×</button><button type="button" class="product-image-lightbox__previous" aria-label="Show previous image">‹</button><img class="product-image-lightbox__image" alt=""><button type="button" class="product-image-lightbox__next" aria-label="Show next image">›</button><span class="product-image-lightbox__count" aria-live="polite"></span>';
    document.body.appendChild(lightbox);
    var lightboxImage = lightbox.querySelector('.product-image-lightbox__image');
    var closeButton = lightbox.querySelector('.product-image-lightbox__close');
    var previousButton = lightbox.querySelector('.product-image-lightbox__previous');
    var nextButton = lightbox.querySelector('.product-image-lightbox__next');
    var count = lightbox.querySelector('.product-image-lightbox__count');
    var currentIndex = 0;
    var render = function (index) {
      currentIndex = thumbs.length ? (index + thumbs.length) % thumbs.length : 0;
      var thumb = thumbs[currentIndex];
      lightboxImage.src = thumb?.dataset.src || image.currentSrc || image.src;
      lightboxImage.alt = thumb?.dataset.alt || image.alt || '';
      var hasMultiple = thumbs.length > 1;
      previousButton.hidden = !hasMultiple;
      nextButton.hidden = !hasMultiple;
      count.hidden = !hasMultiple;
      if (hasMultiple) count.textContent = (currentIndex + 1) + ' / ' + thumbs.length;
    };
    var open = function () {
      var active = thumbs.findIndex(function (thumb) { return thumb.classList.contains('is-active'); });
      render(active >= 0 ? active : 0);
      lightbox.showModal();
      closeButton.focus();
    };
    var close = function () { if (lightbox.open) lightbox.close(); };
    var move = function (step) { render(currentIndex + step); };
    frame.addEventListener('click', function (event) {
      if (event.target.closest('[data-gallery-zoom]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      open();
    }, true);
    closeButton.addEventListener('click', close);
    previousButton.addEventListener('click', function () { move(-1); });
    nextButton.addEventListener('click', function () { move(1); });
    lightbox.addEventListener('click', function (event) { if (event.target === lightbox) close(); });
    lightbox.addEventListener('cancel', function (event) { event.preventDefault(); close(); });
    lightbox.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); move(1); }
    });
  });
});

/* Zoom and drag controls inside the enlarged product image. */
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.product-image-lightbox').forEach(function (lightbox) {
    var image = lightbox.querySelector('.product-image-lightbox__image');
    var previous = lightbox.querySelector('.product-image-lightbox__previous');
    var next = lightbox.querySelector('.product-image-lightbox__next');
    if (!image) return;
    var scale = 1;
    var panX = 0;
    var panY = 0;
    var dragging = false;
    var didDrag = false;
    var startX = 0;
    var startY = 0;
    var startPanX = 0;
    var startPanY = 0;
    var render = function () {
      image.style.transform = 'translate3d(' + panX + 'px,' + panY + 'px,0) scale(' + scale + ')';
      lightbox.classList.toggle('is-image-zoomed', scale > 1);
    };
    var reset = function () { scale = 1; panX = 0; panY = 0; render(); };
    var toggle = function () { scale = scale > 1 ? 1 : 2; if (scale === 1) { panX = 0; panY = 0; } render(); };
    var clamp = function (value, axis) {
      var size = axis === 'x' ? lightbox.clientWidth : lightbox.clientHeight;
      var limit = Math.max(0, (size * (scale - 1)) / 2);
      return Math.max(-limit, Math.min(limit, value));
    };
    image.addEventListener('click', function (event) { if (didDrag) { didDrag = false; event.preventDefault(); return; } if (!dragging) { event.preventDefault(); toggle(); } });
    image.addEventListener('pointerdown', function (event) {
      if (scale <= 1) return;
      dragging = true; didDrag = false; startX = event.clientX; startY = event.clientY; startPanX = panX; startPanY = panY;
      image.setPointerCapture?.(event.pointerId); lightbox.classList.add('is-image-dragging'); event.preventDefault();
    });
    image.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      if (Math.abs(event.clientX - startX) + Math.abs(event.clientY - startY) > 4) didDrag = true;
      panX = clamp(startPanX + event.clientX - startX, 'x'); panY = clamp(startPanY + event.clientY - startY, 'y'); render(); event.preventDefault();
    });
    var endDrag = function (event) { if (!dragging) return; dragging = false; image.releasePointerCapture?.(event.pointerId); lightbox.classList.remove('is-image-dragging'); };
    image.addEventListener('pointerup', endDrag); image.addEventListener('pointercancel', endDrag);
    image.addEventListener('wheel', function (event) { event.preventDefault(); scale = event.deltaY < 0 ? Math.min(3, scale + .25) : Math.max(1, scale - .25); if (scale === 1) { panX = 0; panY = 0; } render(); }, { passive: false });
    previous?.addEventListener('click', reset); next?.addEventListener('click', reset); render();
  });
});
